import { GetterTree, Getter } from "vuex";
import { State } from "@/clickbuy/purchase/store/state";
import { Purchase } from "@/clickbuy/purchase/models/purchase.model";
import { SaleInfo } from "@/clickbuy/purchase/models/sale-info.model";
import { PurchaseLine } from "@/clickbuy/purchase/models/purchase-line.model";
import {
  TypeOfProduct,
  Product,
  TypeOfPrice,
  TypeOfPriceLiteral
} from "@/manager/product/models/product.model";
import { DelegationInterface } from "@/shared/interfaces/delegation.interface";
import { BudgetPaymentMethod } from "@/manager/budget/models/budget.model";

import dayjs, { UnitType } from "dayjs";

export interface Getters {
  getPurchaseList: Purchase[];
  getPurchase: Purchase;
  getSaleInfoList: SaleInfo[];
  getCurrentPurchase: Purchase;
  getConvocationBalance: number;
  currentPurchaseFinished: boolean;
  currentPurchaseHasClient: boolean;
  previousPurchaseIsCanceled: boolean;
  hasPurchases: boolean;
  currentPurchaseHasPurchaseLines: boolean;
  currentPurchaseHasPaymentMethod: boolean;
  getCurrentBudgetAcceptance: any;
  getCurrentBudgetAcceptanceDelegationList: DelegationInterface[];
  getCurrentBudgetAcceptanceDelegationIdsList: string[];
  getCurrentBudgetAcceptanceConvocationFiltered: any[];
  getCurrentBudgetAcceptanceProduct: any;
  getCurrentBudgetAcceptanceProductListContained: any;
  getCurrentBudgetAcceptanceConvocationByProduct: any;
  getCurrentBudgetAcceptanceProductList: any;
  getCurrentBudgetAcceptanceSelectedConvocations: any;
  getCurrentBudgetAcceptancePreviousSelectedConvocations: any;
  getCurrentBudgetAcceptanceProductConvocationSelected: any;
  productConvocationOrderDateDesc: any;
  getCurrentBudgetAcceptanceDiscountSoonPayment: any;
  getCurrentPurchaseTotalPriceWithoutTax: number;
  getCurrentPurchaseSoonPaymentDiscount: number;
  getCurrentPurchaseTotalPrice: number;
  getCurrentPurchaseReserveAmount: number;
  getCurrentPurchaseReserveAmountWithOutTax: number;
  getCurrentPurchasePaymentPending: number;
  getUserMoneyFlow: number;
}

export const getters: GetterTree<State, any> = {
  getPurchaseList: state => state.list,
  getFeeList: state => state.feeList,
  getAllowEditBudget: state => state.allowEditBudget,
  getAllowShowBudgetFees: state => state.allowShowBudgetFees,
  getBudgetList: state => state.budgetList,
  hasPurchases: state => state.list.length > 0,
  previousPurchaseIsCanceled: state =>
    state.list.length > 0 && state.list[0].canceledSuperiorPurchase,
  hasPreviousPurchaseNotCanceled(state: State, getters: Getters) {
    return (
      getters.currentPurchaseHasClient &&
      getters.hasPurchases &&
      !getters.previousPurchaseIsCanceled &&
      !getters.getCurrentPurchase.addNewPurchase
    );
  },
  getPurchase(state: State) {
    return (id: string) => {
      return state.list.find((purchase: any) => purchase.id === id);
    };
  },
  getBudget(state: State) {
    return (id: string) => {
      return state.budgetList.find((budget: any) => budget.id === id);
    };
  },
  getSaleInfoList: state => state.saleInfoList,
  getTypeOfPrice: state => state.typeOfPrice,
  getSaleInfoDefault: state =>
    state.saleInfoList.find((saleInfo: any) => {
      return saleInfo.product.default === true;
    }),
  getSaleInfo(state: State) {
    return (id: string) => {
      return state.saleInfoList.find((saleInfo: any) => saleInfo.id === id);
    };
  },
  getCompanion(state: State) {
    return (id: string) => {
      return state.companionList.find((companion: any) => companion.id === id);
    };
  },
  getConvocationBalance: state => state.convocationBalance,
  addingUser: state => state.addingUser,
  getSelectedClient: state => state.selectedClient,
  getInternalBussinessSelected: state => state.internalBussinessSelected,
  getCurrentPurchase: state => state.currentPurchase,
  currentPurchaseFinished(state: State): boolean {
    return state.currentPurchase.id !== undefined;
  },
  currentPurchaseHasClient(state: State): boolean {
    return (state.currentPurchase.client as any).user.id !== undefined;
  },
  currentPurchaseHasPurchaseLines(state: State): boolean {
    return state.currentPurchase.purchaseLines.length > 0;
  },
  currentPurchaseHasPaymentMethod(state: State): boolean {
    var result = true
    if (state.currentPurchase.paymentMethod == undefined) {
      result = false
    }

    if (state.currentPurchase.paymentMethod == 2) {
      if (!(state.currentPurchase.cardLastDigits !== undefined &&
        state.currentPurchase.cardLastDigits.length == 4)) {
        result = false
      }
    }

    return result
  },

  currentPurchaseIsComplete(state: State, getters: Getters): boolean {
    return (
      getters.currentPurchaseHasClient &&
      getters.currentPurchaseHasPaymentMethod &&
      getters.currentPurchaseHasPurchaseLines
    );
  },
  currentPurchaseGiftTotalAmount(state: State): number {
    return state.currentPurchase.purchaseLines.reduce(
      (total: number, line: PurchaseLine) => {
        if (line.product) {
          const bonus = (line.product as any).bonus;
          if (bonus && bonus.length > 0) {
            return total + bonus[0].totalAmountGift * line.amount;
          } else {
            return total;
          }
        } else {
          return total;
        }
      },
      0
    );
  },
  getCurrentPurchaseTypeOfProduct: state => state.currentPurchaseTypeOfProduct,
  currentPurchaseTypeOfProductIsCourse(state): boolean {
    return state.currentPurchaseTypeOfProduct === TypeOfProduct.Course;
  },
  currentPurchaseTypeOfProductIsMerchandising(state): boolean {
    return state.currentPurchaseTypeOfProduct === TypeOfProduct.Merchandising;
  },
  currentPurchaseTypeOfProductIsBook(state): boolean {
    return state.currentPurchaseTypeOfProduct === TypeOfProduct.Book;
  },
  getCurrentPurchaseTotalPriceWithoutTaxNoMoneyFlows(state): number {
    let total = 0;
    if (state.currentPurchase.purchaseLines.length > 0) {
      const typeOfPrice = TypeOfPriceLiteral[state.typeOfPrice];
      total = state.currentPurchase.purchaseLines.reduce(
        (total: number, line: any) => {
          if (line.product) {
            if (line.repeater) {
              return total + line.product.repeat * line.amount;
            } else if (line.product.reserve > 0) {
              let reserveAmount = line.product.reserve * line.amount;
              if (
                state.currentPurchase.reserveAmountTouched &&
                state.currentPurchase.reserveAmount
              ) {
                reserveAmount = state.currentPurchase.reserveAmount;
              }
              return total + reserveAmount;
            } else {
              return total + line.product[typeOfPrice] * line.amount;
            }
          } else {
            return total;
          }
        },
        0
      );
    }

    return total;
  },

  getterPurchaseLinesTotalPrice(state, getters): number {
    let total = 0;
    const typeOfPrice = TypeOfPriceLiteral[state.typeOfPrice];
    if (state.currentPurchase.purchaseLines.length > 0) {
      total = state.currentPurchase.purchaseLines.reduce(
        (total: number, line: any) => {
          if (line.product) {
            const taxForm = line.product.tax / 100;
            if (line.repeater) {
              const tax = line.product.repeat * line.amount * taxForm;
              return total + line.product.repeat * line.amount + tax;
            } else {
              const tax = line.product[typeOfPrice] * line.amount * taxForm;
              return total + line.product[typeOfPrice] * line.amount + tax;
            }
          } else {
            return total;
          }
        },
        0
      );
    }
    return total;
  },

  getCurrentPurchaseTotalPriceWithoutTax(state, getters): number {
    let total = 0;
    total = getters.getCurrentPurchaseTotalPriceWithoutTaxNoMoneyFlows;
    total = getters.checkConvocationBalance(total);
    total = getters.checkClientMoneyFlow(total);

    return total;
  },
  getCurrentPurchaseSoonPaymentDiscount(state, getters): number {
    let total = 0;
    if (state.currentPurchase.reserveAmount != 0 && state.currentPurchase.purchaseLines.length > 0 && state.currentPurchase.soonPaymentPurchaseCheck) {
      total = state.currentPurchase.purchaseLines.reduce(
        (total: number, line: any) => {
          return total + (line.product.discountSoonPayment * line.amount);
        },
        0
      );
    }
    // total = getters.checkClientMoneyFlow(total);
    return total;
  },
  getCurrentPurchaseTotalPrice(state, getters): number {
    let total = 0;
    if (!state.currentPurchase.reserveAmountTouched) {
      if (state.currentPurchase.purchaseLines.length > 0) {
        total = state.currentPurchase.purchaseLines.reduce(
          (total: number, line: any) => {
            if (line.reserve > 0) {
              return total + line.totalPrice * line.amount;
            } else {
              return total + line.totalPrice;
            }
          },
          0
        );
      }
      total = getters.checkClientMoneyFlow(total);

      if (state.currentPurchase.purchaseLines.length == 0 && state.currentPurchase.previousReserveAmount && state.currentPurchase.previousReserveAmount > 0) {
        return -state.currentPurchase.previousReserveAmount;
      } else if (!state.currentPurchase.soonPaymentPurchaseCheck && state.currentPurchase.previousReserveAmount) {
        if ((total - state.currentPurchase.previousReserveAmount) < 0) {
          return 0;
        } else {
          return total - state.currentPurchase.previousReserveAmount
        }
      }
      return total;
    } else if (state.currentPurchase.modifyPurchase && state.currentPurchase.soonPaymentPurchaseCheck &&
      state.currentPurchase.reserveAmount && state.currentPurchase.previousReserveAmount &&
      ((state.currentPurchase.reserveAmount - getters.getCurrentPurchaseSoonPaymentDiscount) <= state.currentPurchase.previousReserveAmount)) {
      // Cuando se modifica una compra Pronto Pago y el Pago Inicial es menor que el Pago Previo
      return getters.getterPurchaseLinesTotalPrice - state.currentPurchase.previousReserveAmount - getters.getCurrentPurchaseSoonPaymentDiscount
    } else if (state.currentPurchase.modifyPurchase && state.currentPurchase.previousReserveAmount) {
      return (state.currentPurchase.reserveAmount || 0) - getters.getCurrentPurchaseSoonPaymentDiscount - state.currentPurchase.previousReserveAmount;
    } else return (state.currentPurchase.reserveAmount || 0) - getters.getCurrentPurchaseSoonPaymentDiscount; //??
  },
  getCurrentPurchaseReserveAmount(state, getters): number {
    let total = 0;
    const typeOfPrice = TypeOfPriceLiteral[state.typeOfPrice];
    if (!state.currentPurchase.reserveAmountTouched) {
      if (state.currentPurchase.purchaseLines.length > 0) {
        total = state.currentPurchase.purchaseLines.reduce(
          (total: number, line: any) => {
            if (line.product && line.product.reserve > 0) {
              const tax =
                parseFloat((line.product.reserve * (line.product.tax / 100) * line.amount).toFixed(2)) 
              return total + line.product.reserve * line.amount + tax;
              // } else if (line.product.reserve == 0 && line.product[typeOfPrice] && state.currentPurchase.soonPaymentPurchaseCheck) {
            } else if (line.product.reserve == 0 && line.product[typeOfPrice]) {
              const tax =
                parseFloat((line.product.reserve * (line.product.tax / 100) * line.amount).toFixed(2)) 
              return total + line.product[typeOfPrice] * line.amount + tax;
            } else {
              return total;
            }
          },
          0
        );
      }
    } else if (state.currentPurchase.reserveAmount) {
      if (state.currentPurchase.soonPaymentPurchaseCheck && state.currentPurchase.modifyPurchase && state.currentPurchase.previousReserveAmount &&
        (state.currentPurchase.reserveAmount < state.currentPurchase.previousReserveAmount)) {
        total = state.currentPurchase.previousReserveAmount;
        return total;
      }
      total = state.currentPurchase.reserveAmount;
    }
    //Previous purchase has custom initial payment
    if (state.currentPurchase.modifyPurchase && state.currentPurchase.previousReserveAmount && total < state.currentPurchase.previousReserveAmount) {
      return state.currentPurchase.previousReserveAmount
    }
    return total;
  },
  getCurrentPurchaseReserveAmountWithOutTax(state): number {
    let total = 0;
    if (!state.currentPurchase.reserveAmountTouched) {
      if (state.currentPurchase.purchaseLines.length > 0) {
        total = state.currentPurchase.purchaseLines.reduce(
          (total: number, line: any) => {
            if (line.product && line.product.reserve > 0) {
              return total + line.product.reserve * line.amount;
            } else {
              return total;
            }
          },
          0
        );
      }
    } else if (state.currentPurchase.reserveAmount) {
      total = state.currentPurchase.reserveAmount;
    }
    return total;
  },
  getPurchaseTagList(state): Array<Record<string, string>> {
    return state.purchaseTagList;
  },
  getCurrentPurchasePaymentPending(state, getters): number {
    let total = 0;
    const typeOfPrice = TypeOfPriceLiteral[state.typeOfPrice];
    if (state.currentPurchase.purchaseLines.length > 0) {
      total = state.currentPurchase.purchaseLines.reduce(
        (total: number, line: any) => {
          if (line.product && line.product.reserve > 0) {
            const taxForm = (line.product.tax / 100 || 0);
            if (line.repeater) {
              const tax = line.product.repeat * line.amount * taxForm;
              return total + line.product.repeat * line.amount + tax;
            } else {
              const tax = line.product[typeOfPrice] * line.amount * taxForm;
              return total + line.product[typeOfPrice] * line.amount + tax;
            }
          } else if ((line.product.reserve == 0 && line.product[typeOfPrice]) || state.currentPurchase.soonPaymentPurchaseCheck || state.currentPurchase.modifyPurchase) {
            const taxForm = (line.product.tax / 100 || 0);
            const tax = line.product[typeOfPrice] * line.amount * taxForm;
            return total + line.product[typeOfPrice] * line.amount + tax;
          } else {
            return total;
          }
        },
        0
      );
      // total -= state.currentPurchase.reserveAmount || 0;
      // total -= state.currentPurchase.reserveAmount || 0;
    }
    total = getters.checkMoneyFlowAvailable(total);
    return total;
  },
  getTotalToPayNotReserve(state) {
    const typeOfPrice = TypeOfPriceLiteral[state.typeOfPrice];
    const totalNotReserve = state.currentPurchase.purchaseLines.reduce(
      (total: number, line: any) => {
        if (
          line.product &&
          (line.product.reserve === 0 || line.product.reserve === null)
        ) {
          const taxForm = line.product.tax / 100;
          if (line.repeater) {
            const tax = line.product.repeat * line.amount * taxForm;
            return total + line.product.repeat * line.amount + tax;
          } else {
            const tax = line.product[typeOfPrice] * line.amount * taxForm;
            return total + line.product[typeOfPrice] * line.amount + tax;
          }
        } else {
          return total;
        }
      },
      0
    );
    return totalNotReserve;
  },
  checkMoneyFlowAvailable(state, getters) {
    return (total: number) => {
      const toPayWithoutMoneyFlow =
        getters.getCurrentPurchaseTotalPriceWithoutTaxNoMoneyFlows;
      const totalMoneyFlow = state.userMoneyFlow;
      const reserve = (getters.getCurrentPurchaseReserveAmount > (state.currentPurchase.previousReserveAmount || 0)) ? getters.getCurrentPurchaseReserveAmount : state.currentPurchase.previousReserveAmount;
      let remainingMoneyFlow = totalMoneyFlow - toPayWithoutMoneyFlow;
      if (state.convocationBalance === 0) {
        const toPayNotReserve = getters.getTotalToPayNotReserve;
        remainingMoneyFlow = totalMoneyFlow - toPayNotReserve;
      }
      let toSubstract = reserve;
      if (remainingMoneyFlow > 0) {
        toSubstract += remainingMoneyFlow;
      }
      if (total >= toSubstract) {
        total -= toSubstract;
      } else {
        total = 0;
      }
      return total;
    };
  },
  checkConvocationBalance(state: State) {
    return (total: number) => {
      let result = total;
      if (total > 0 && state.convocationBalance > 0) {
        result = 0;
        const calc = state.convocationBalance - total;
        if (calc < 0) {
          result = Math.abs(calc);
        }
      }
      return result;
    };
  },
  checkClientMoneyFlow(state, getters) {
    return (total: number) => {
      const moneyFlowAvailable = state.userMoneyFlow - state.convocationBalance;
      let result = total;
      if (total > 0 && moneyFlowAvailable > 0) {
        const totalNotReserve = getters.getTotalToPayNotReserve;
        if (state.convocationBalance === 0) {
          if (totalNotReserve > 0) {
            const calc = totalNotReserve - moneyFlowAvailable;
            if (calc < 0) {
              result = total - totalNotReserve;
            } else {
              result = total - totalNotReserve + calc;
            }
          }
        } else {
          result = total - moneyFlowAvailable - getters.getCurrentPurchaseSoonPaymentDiscount;
          if (result < 0) {
            result = 0;
          }
        }
      }
      return result - getters.getCurrentPurchaseSoonPaymentDiscount;
    };
  },
  getBudgetNote(state: State) {
    return (noteId: string) => {
      return state.budgetNoteList.find((note: any) => note.id === noteId);
    };
  },
  getPurchaseNote(state: State) {
    return (noteId: string) => {
      return state.purchaseNoteList.find((note: any) => note.id === noteId);
    };
  },
  getBudgetNotes(state: State) {
    return (budgetId: string) => {
      return state.budgetNoteList.filter(
        (note: any) => note.budgetId === budgetId
      );
    };
  },
  getPurchaseNotes(state: State) {
    return (purchaseId: string) => {
      return state.purchaseNoteList.filter(
        (note: any) => note.purchaseId === purchaseId
      );
    };
  },
  getBudgetCommentNotes(state: State) {
    return (noteId: string) => {
      return state.budgetCommentNoteList.filter(
        (comment: any) => comment.noteId === noteId
      );
    };
  },
  getCurrentBudget: state => state.currentBudget,
  editingCurrentBudget: state => state.currentBudget.id !== undefined,
  currentBudgetHasLastLineEmpty(state: State): boolean {
    const lines = (state.currentBudget as any).budgetLine;
    const linesLength = lines.length;
    return lines[linesLength - 1].product.id === undefined;
  },
  currentBudgetLastLineProduct(state: State): boolean {
    const lines = (state.currentBudget as any).budgetLine;
    const linesLength = lines.length;
    return lines[linesLength - 1].product;
  },
  currentBudgetHasLineProduct(state: State): boolean {
    const lines = (state.currentBudget as any).budgetLine;
    const filter = lines.filter((line: any) => {
      return line.product.id && line.amount > 0;
    });
    return filter.length > 0;
  },
  getCurrentBudgetAcceptance: state => state.currentBudgetAcceptance,
  currentBudgetAcceptanceHasMoneyFlowDiscount: (state: State) => {
    return (state.currentBudgetAcceptance as any).moneyFlowDiscount > 0;
  },
  getCurrentBudgetAcceptanceDiscountSoonPayment: (
    state: State,
    getters: Getters
  ) => {
    const budget = state.currentBudgetAcceptance;
    let discount = 0;
    if ((budget.budgetFee as any).length === 1) {
      getters.getCurrentBudgetAcceptanceProductList.map((product: Product) => {
        const productDiscount = product.discountSoonPayment;
        if (discount < Number(productDiscount)) {
          discount = Number(productDiscount);
        }
      });
    }
    return discount;
  },
  getCurrentBudgetAcceptanceTotalPrice: (state: State, getters: Getters) => {
    const budget = state.currentBudgetAcceptance;
    const discount = getters.getCurrentBudgetAcceptanceDiscountSoonPayment;
    const total = Number(budget.totalPrice) - discount;
    return total;
  },
  getCurrentBudgetAcceptanceDelegationList: state =>
    state.currentBudgetAcceptanceDelegationList,
  currentBudgetAcceptanceIsExpired: state => {
    const date = dayjs(state.currentBudgetAcceptance.dateExpiration);
    const today = dayjs();
    return date.isBefore(today);
  },
  getCurrentBudgetAcceptanceDelegationIdsList: state => {
    return state.currentBudgetAcceptanceDelegationList.map(
      (delegation: any) => delegation.id
    );
  },
  getCurrentBudgetAcceptanceDelegation(state: State) {
    return (delegationId: string) => {
      return state.currentBudgetAcceptanceDelegationList.find(
        (delegation: any) => delegation.id === delegationId
      );
    };
  },
  getCurrentBudgetAcceptanceConvocationList: state =>
    state.currentBudgetAcceptanceConvocationList,
  getCurrentBudgetAcceptanceConvocationFiltered(
    state: State,
    getters: Getters
  ) {
    return state.currentBudgetAcceptanceConvocationList.filter(
      (convocation: any) => {
        return getters.getCurrentBudgetAcceptanceDelegationList.find(
          (delegation: any) => {
            return true;
            // return (
            //   convocation.delegation.id === delegation.id ||
            //   convocation.product.onlyTeachDelegation.length > 0
            // );
          }
        );
      }
    );
  },
  getCurrentBudgetAcceptanceConvocationByProduct(
    state: State,
    getters: Getters
  ) {
    return (productId: string) => {
      return getters.getCurrentBudgetAcceptanceConvocationFiltered.filter(
        (convocation: any) => convocation.product.id === productId
      );
    };
  },
  getCurrentBudgetAcceptanceProductList: state =>
    state.currentBudgetAcceptanceProductList,
  getCurrentBudgetAcceptanceProduct(state: State) {
    return (productId: string) => {
      return state.currentBudgetAcceptanceProductList.find(
        (product: any) => product.id === productId
      );
    };
  },
  budgetProductsPack(state: State) {
    return state.budgetProductsPack;
  },
  getCurrentBudgetAcceptanceProductListContained(
    state: State,
    getters: Getters
  ) {
    return (productId: string) => {
      const product = getters.getCurrentBudgetAcceptanceProduct(productId);
      let productList: any[] = [];
      if (product.pack) {
        productList = product.pack.productInPack;
      } else {
        productList = [product];
      }
      return productList;
    };
  },
  getCurrentBudgetAcceptanceProductConvocationSelected(
    state: State,
    getters: Getters
  ) {
    return (productId: string) => {
      let productConvocation;
      const selectedConvocations =
        getters.getCurrentBudgetAcceptanceSelectedConvocations;
      const productConvocations = getters.getCurrentBudgetAcceptanceConvocationByProduct(
        productId
      );
      if (productConvocations.length > 0) {
        selectedConvocations.forEach((convocationId: string) => {
          productConvocations.forEach((convocation: any) => {
            if (convocationId === convocation.id) {
              productConvocation = convocation;
            }
          });
        });
      } else {
        const budgetAcceptance = getters.getCurrentBudgetAcceptance;
        if (budgetAcceptance.convocation) {
          productConvocation = budgetAcceptance.convocation.find(
            (conv: any) => {
              return conv.product === productId;
            }
          );
        }
      }
      return productConvocation;
    };
  },
  productConvocationOrderDateDesc() {
    return (list: any[]) => {
      return list.sort((a: any, b: any) => {
        if (a.convocation && b.convocation) {
          const aDate = dayjs(a.convocation.timetables[0].date);
          const bDate = dayjs(b.convocation.timetables[0].date);
          if (bDate.isBefore(aDate)) {
            return 1;
          } else {
            return -1;
          }
        } else {
          return -1;
        }
      });
    };
  },
  getCurrentBudgetAcceptanceProductsWithConvocation(
    state: State,
    getters: Getters
  ) {
    const productWithConvocationList: any[] = [];
    const productNotInPack: any[] = [];
    const productList = getters.getCurrentBudgetAcceptanceProductList;
    productList.forEach((rootProduct: any) => {
      const emptyContainedProducts: any[] = [];
      const newObj = {
        product: rootProduct,
        containedProducts: emptyContainedProducts
      };
      const containedProducts = getters.getCurrentBudgetAcceptanceProductListContained(
        rootProduct.id
      );
      containedProducts.forEach((product: any) => {
        const convocation = getters.getCurrentBudgetAcceptanceProductConvocationSelected(
          product.id
        );
        newObj.containedProducts.push({ product, convocation });
      });
      if (newObj.containedProducts.length > 1) {
        newObj.containedProducts = getters.productConvocationOrderDateDesc(
          newObj.containedProducts
        );
      }
      if (rootProduct.pack) {
        productWithConvocationList.push(newObj);
      } else {
        productNotInPack.push(newObj.containedProducts[0]);
      }
    });
    const notInPack = { product: {}, containedProducts: productNotInPack };
    notInPack.containedProducts = getters.productConvocationOrderDateDesc(
      notInPack.containedProducts
    );
    productWithConvocationList.push(notInPack);
    return productWithConvocationList;
  },
  getCurrentBudgetAcceptanceBranchProductList(state: State, getters: Getters) {
    return (productId: string, branch: string | null = null) => {
      const productList = getters.getCurrentBudgetAcceptanceProductListContained(
        productId
      );
      if (branch) {
        return productList
          .filter((product: any) => {
            return product.formativeBranch === branch;
          })
          .sort((a: any, b: any) => {
            return a.level - b.level;
          });
      } else {
        return productList.filter((product: any) => {
          return product.formativeBranch === null;
        });
      }
    };
  },
  getCurrentBudgetAcceptanceProductListByLevel(state: State, getters: Getters) {
    return (productId: string, level: number) => {
      return getters
        .getCurrentBudgetAcceptanceProductListContained(productId)
        .filter((product: any) => {
          return product.level === level && product.formativeBranch !== null;
        })
        .sort((a: any, b: any) => {
          return a.formativeBranch - b.formativeBranch;
        });
    };
  },
  getCurrentBudgetAcceptanceSelectedConvocations: state =>
    state.currentBudgetAcceptanceSelectedConvocations,
  getCurrentBudgetAcceptancePreviousSelectedConvocations: state =>
    state.currentBudgetAcceptancePreviousSelectedConvocations,
  getAcceptanceTimeout: state => state.acceptanceRequestTimeout,
  isCardMethodSelectedInCurrentBudgetAcceptance(state) {
    return (
      Number(state.currentBudgetAcceptance.paymentMethod) ===
      BudgetPaymentMethod.Card
    );
  },
  isDirectDebitMethodSelectedInCurrentBudgetAcceptance(state) {
    return (
      Number(state.currentBudgetAcceptance.paymentMethod) ===
      BudgetPaymentMethod.DirectDebit
    );
  },
  isWireTransferMethodSelectedInCurrentBudgetAcceptance(state) {
    return (
      Number(state.currentBudgetAcceptance.paymentMethod) ===
      BudgetPaymentMethod.SendWireTransfer
    );
  },
  getCurrentBudgetAcceptanceMessageInfo: state =>
    state.currentBudgetAcceptanceMessageInfo,
  getUserMoneyFlow: state => state.userMoneyFlow,
  hasUserMoneyFlow: state => state.userMoneyFlow > 0,
  getCurrentPurchaseLoading: state => state.currentPurchaseLoading,
  getAcceptanceFieldError: state => state.acceptanceFieldError
};
